import React, { useState, useRef, useEffect } from 'react';
import { AsyncTypeahead, TypeaheadResult } from 'react-bootstrap-typeahead';
import { CustomError } from './CustomError';
import { getCountryCode } from '../../../../../../../selectors/generic';
import {translate} from "../../../../../../../api/utils/i18nHelper";
import {store} from "../../../../../../../config/redux.storage";

const azureMapsKey = 'sTxM0XHmq5XxWj1ZFl5L-L0TmezILCMRKJNPsr0hdTo';
const countrySet = getCountryCode();
const baseUrl = `https://atlas.microsoft.com/search/address/json?api-version=1.0&subscription-key=${azureMapsKey}`;
const SEARCH_URI = countrySet
    ? `${baseUrl}&countrySet=${countrySet}`
    : baseUrl;

interface Option {
    label: string;
    city?: string;
    postalCode?: string;
    streetName?: string;
    streetNumber?: string;
    box?: string;
    lat: number;
    lon: number;
}

interface AzureMapsAutocompleteProps {
    idSchema?: any;
    required?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    autofocus?: boolean;
    onChange?: (value: any) => void;
    onBlur?: (id: any, value: any) => void;
    schema?: any;
    uiSchema?: any;
    formData?: any;
    onChildChange?: (parentObject: string, value: any) => void;
}

const AzureMapsAutocomplete: React.FC<AzureMapsAutocompleteProps> = ({
    ...props
}) => {
    const {
        idSchema,
        required,
        disabled,
        readonly,
        autofocus,
        onChange,
        onBlur,
        schema,
        uiSchema,
        formData,
        onChildChange,
    } = props;

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [optionsLocation, setOptionsLocation] = useState<Option[]>([]);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { i18n } = store.getState().myPuratos_i18n;

    const fetchSearchResults = (query: string) => {
        setIsLoading(true);

        fetch(`${SEARCH_URI}&query=${query}&typeahead=true&limit=4`)
            .then((resp) => resp.json())
            .then((data) => {
                const results = data.results.map((item: any) => {
                    const address = item.address || {};
                    return {
                        label: address.freeformAddress || '',
                        city: address.municipality || '',
                        postalCode:
                            address.extendedPostalCode ||
                            address.postalCode ||
                            '',
                        streetName: address.streetName || '',
                        streetNumber: address.streetNumber || '',
                        box: '',
                        state:
                            address.countrySubdivisionCode ||
                            address.countrySecondarySubdivision ||
                            '',
                        lat: item.position.lat || 0,
                        lon: item.position.lon || 0,
                    };
                });

                results.push({
                    label: schema?.manualSelection || 'Enter address manually',
                    lat: 0,
                    lon: 0,
                });

                setOptionsLocation(results);
                setIsLoading(false);
            })
            .catch((error) => {
                if (onChildChange && schema?.objToFill)
                    onChildChange(schema.objToFill, {
                        label:
                            schema?.manualSelection || 'Enter address manually',
                        lat: 0,
                        lon: 0,
                    });
                console.error('Error fetching data:', error);
                setIsLoading(false);
            });
    };

    const debounce = (func: Function, delay: number) => {
        return function(...args: any) {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            debounceTimeoutRef.current = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    const debouncedFetchSearchResults = debounce(fetchSearchResults, 800);

    const handleSearch = (query: string) => {
        if (query.length >= 2) {
            debouncedFetchSearchResults(query);
        }
    };

    const handleOptionSelected = (selected: Option[]) => {
        if (selected.length > 0 && onChildChange && schema?.objToFill) {
            onChildChange(schema.objToFill, selected[0]);
        }
    };

    const filterBy = () => true;

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    if (formData && formData.trim()) {
        return null; // Hide the component if formData has a value
    }

    return (
        <>
            {schema.title && (
                <label
                    className={'control-label'}
                    htmlFor={idSchema.$id}
                >
                    {schema.title}
                </label>
            )}
            <AsyncTypeahead
                filterBy={filterBy}
                id="azure-maps-autocomplete"
                inputProps={{ id: idSchema.$id }}
                isLoading={isLoading}
                labelKey="label"
                className="custom-field"
                minLength={2}
                name={idSchema.$id}
                onSearch={handleSearch}
                options={optionsLocation}
                required={required}
                disabled={disabled}
                promptText={translate(i18n, "Type to search") || "Type to search"}
                searchText={translate(i18n, "Searching") || "Searching..."}
                readOnly={readonly}
                autoFocus={autofocus}
                value={formData}
                placeholder={uiSchema['ui:placeholder']}
                onChange={handleOptionSelected}
                renderMenuItemChildren={(option: TypeaheadResult<Option>) => (
                    <>
                        <span>{option.label}</span>
                    </>
                )}
            />
            <CustomError schema={schema} uiSchema={uiSchema} />
        </>
    );
};

export default AzureMapsAutocomplete;
